import { Box, Collapse, Typography } from '@mui/material';
import AddressLookup from '@acorn-portal/shared/src/components/addressLookup';
import RadioGroupButton from '@acorn-portal/shared/src/components/inputs/radio-button-group';
import FormFieldOptions from '@acorn-portal/shared/src/components/inputs/FormFieldOptions';
import FormTextField from '@acorn-portal/shared/src/components/inputs/FormTextField';
import { useFormikContext } from 'formik';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { Address } from '@acorn-portal/shared/src/services/api';
import { setCardValidFlag, setCardPaymentErrorMessage } from '@acorn-portal/shared/src/store/cardPaymentSlice';
import { CARD_PAYMENT_FROM_ERRORS } from '@acorn-portal/shared/src/utils/constants/ErrorMessages';
import { NAME_REGEX } from '@acorn-portal/shared/src/utils/constants/Regex';
import * as Yup from 'yup';
import BraintreeFormInput from '@acorn-portal/shared/src/components/payment/braintree-form-input';
import ScrollToError from '@acorn-portal/shared/src/components/scroll-to-error';

export interface IBraintreeCardPaymentFormValues {
	billingDetails: string;
	cardholderFirstName: string;
	cardholderLastName: string;
	cardholderEmail: string;
	cardholderAddress: Address;
	saveCard: boolean;
}

interface IPolicyHolder {
	firstName: string;
	lastName: string;
	email: string;
	address: Address;
}

interface IProps {
	policyHolder: IPolicyHolder;
	amountToPay: string;
	saveCardWarning?: string;
}

export const braintreeCardFormValidationSchema = Yup.object({
	billingDetails: Yup.string().required('Billing details required'),
	cardholderFirstName: Yup.string().when('billingDetails', {
		is: 'no',
		then: (schema) =>
			schema
				.matches(NAME_REGEX, CARD_PAYMENT_FROM_ERRORS.cardholderNameSpecialChar)
				.required(CARD_PAYMENT_FROM_ERRORS.cardholderFirstNameRequired)
				.min(3, CARD_PAYMENT_FROM_ERRORS.cardholderFirstNameMinLength)
				.max(50, CARD_PAYMENT_FROM_ERRORS.cardholderFirstNameMaxLength),
		otherwise: (schema) => schema.notRequired(),
	}),
	cardholderLastName: Yup.string().when('billingDetails', {
		is: 'no',
		then: (schema) =>
			schema
				.matches(NAME_REGEX, CARD_PAYMENT_FROM_ERRORS.cardholderNameSpecialChar)
				.required(CARD_PAYMENT_FROM_ERRORS.cardholderLastNameRequired)
				.min(3, CARD_PAYMENT_FROM_ERRORS.cardholderLastNameMinLength)
				.max(50, CARD_PAYMENT_FROM_ERRORS.cardholderLastNameMaxLength),
		otherwise: (schema) => schema.notRequired(),
	}),
	cardholderEmail: Yup.string().when('billingDetails', {
		is: 'no',
		then: (schema) => schema.email('Please enter a valid email address').required('Email address is a required field'),
		otherwise: (schema) => schema.notRequired(),
	}),
});

export const BRAINTREE_FORM_INITIAL_VALUES: IBraintreeCardPaymentFormValues = {
	billingDetails: '',
	cardholderFirstName: '',
	cardholderLastName: '',
	cardholderEmail: '',
	cardholderAddress: {
		line1: '',
		line2: '',
		line3: '',
		postcode: '',
		city: '',
		country: '',
	},
	saveCard: true,
};

export const useBraintreePaymentFromState = () => {
	const dispatch = useAppDispatch();
	const { isCardValid, cardPaymentError } = useAppSelector((redux) => redux.cardPayment);

	const setBraintreeError = (error: string | undefined) => {
		dispatch(setCardPaymentErrorMessage(error));
	};

	const setCardValid = (isValid: boolean) => {
		dispatch(setCardValidFlag(isValid));
	};

	return { cardPaymentError, isCardValid, setBraintreeError, setCardValid };
};

const BraintreePaymentForm = ({ policyHolder, amountToPay, saveCardWarning }: IProps) => {
	const formik = useFormikContext<IBraintreeCardPaymentFormValues>();
	const { setBraintreeError } = useBraintreePaymentFromState();
	return (
		<ScrollToError>
			<Box sx={{ margin: 'auto' }}>
				<RadioGroupButton
					label='Is the debit/credit card registered to the same address?'
					name='billingDetails'
					id='billingDetails'
					value={formik.values.billingDetails}
					onChange={(value) => {
						if (value === 'yes') {
							formik.setValues({
								...formik.values,
								cardholderFirstName: policyHolder?.firstName,
								cardholderLastName: policyHolder?.lastName,
								cardholderEmail: policyHolder.email,
								cardholderAddress: policyHolder?.address,
							});
						} else {
							formik.setValues({
								...formik.values,
								cardholderFirstName: '',
								cardholderLastName: '',
								cardholderEmail: '',
								cardholderAddress: {
									line1: '',
									line2: '',
									line3: '',
									postcode: '',
									city: '',
									country: '',
								},
							});
						}
						formik.setFieldValue('billingDetails', value);
						setTimeout(() => {
							formik.validateField('cardholderAddress');
							formik.validateField('cardholderFirstName');
							formik.validateField('cardholderLastName');
							formik.validateField('cardholderEmail');
						}, 100);
					}}
					error={formik.touched.billingDetails && Boolean(formik.errors.billingDetails)}
					helperText={formik.touched.billingDetails && formik.errors.billingDetails}
				/>
				<Collapse in={formik.values.billingDetails === 'no'}>
					<Box>
						<Typography variant='h6' gutterBottom>
							Billing Details
						</Typography>
						<FormTextField
							id='cardholderFirstName'
							name='cardholderFirstName'
							label='Cardholder First Name'
							value={formik.values.cardholderFirstName}
							onChange={formik.handleChange}
							error={formik.touched.cardholderFirstName && Boolean(formik.errors.cardholderFirstName)}
							helperText={formik.touched.cardholderFirstName && formik.errors.cardholderFirstName}
						/>
						<FormTextField
							id='cardholderLastName'
							name='cardholderLastName'
							label='Cardholder Last Name'
							value={formik.values.cardholderLastName}
							onChange={formik.handleChange}
							error={formik.touched.cardholderFirstName && Boolean(formik.errors.cardholderLastName)}
							helperText={formik.touched.cardholderLastName && formik.errors.cardholderLastName}
						/>
						<FormTextField
							autoComplete='email'
							format='email'
							id='cardholderEmail'
							name='cardholderEmail'
							label='Cardholder Email'
							value={formik.values.cardholderEmail}
							onChange={formik.handleChange}
							error={formik.touched.cardholderEmail && Boolean(formik.errors.cardholderEmail)}
							helperText={formik.touched.cardholderEmail && formik.errors.cardholderEmail}
						/>
						<Typography variant='h6' gutterBottom sx={{ textAlign: 'left', fontSize: 18 }}>
							Billing Address
						</Typography>
						<AddressLookup
							globalError={formik.touched.cardholderAddress && Boolean(formik.errors.cardholderAddress)}
							initialValue={formik.values.cardholderAddress}
							onChange={(value) => {
								formik.setFieldValue('cardholderAddress', value);
							}}
							firstFieldLabel='Cardholder house number or name'
							postcodeLabel='Cardholder postcode'
						/>
					</Box>
				</Collapse>
			</Box>
			<Box sx={{ mt: 2 }}>
				<Typography variant='h6' gutterBottom>
					Card Details
				</Typography>
				<Typography sx={{ margin: 'auto', paddingBottom: '16px' }}>
					We will securely save your payment card details so that any future payments to your policy can be easily
					taken. This includes any changes to your policy in the future. You can opt out of this by selecting the
					options below:
				</Typography>
				<FormFieldOptions
					topLabel
					name='saveCard'
					label=''
					value={formik.values.saveCard}
					onChange={(event) => {
						const { value } = event.target;
						formik.setFieldValue('saveCard', value === 'true');
					}}
					options={[
						{
							label: (
								<Typography>
									<b>Save my card details</b> - We will keep your card details stored safely and securely to allow us to
									take any future payments
								</Typography>
							),
							value: true,
						},
						{
							label: (
								<>
									<Typography>
										<b>Don&apos;t save my card details</b> - We won&apos;t store your card details and you&apos;ll need
										to give us your payment card details every time you make a change
									</Typography>
									{saveCardWarning && formik.values.saveCard === false && (
										<Typography sx={{ mt: 2 }} color='error.main'>
											{saveCardWarning}
										</Typography>
									)}
								</>
							),
							value: false,
						},
					]}
				/>
				<BraintreeFormInput
					amount={amountToPay}
					isOptOut={!formik.values.saveCard}
					setError={(error) => setBraintreeError(error)}
				/>
			</Box>
		</ScrollToError>
	);
};

export default BraintreePaymentForm;
